import { useMemo } from 'react';
import { FormattedMessage, FormattedRelativeTime } from 'react-intl';

import AccessTime from '@mui/icons-material/AccessTime';
import Done from '@mui/icons-material/Done';
import { Stack } from '@mui/material';

import { differenceInDays, formatDistanceToNow, parseISO } from 'date-fns';

import { getMinSharesToBuy, getPercentFunded } from '@arrived/common';
import { useIsOfferingBlocked } from '@arrived/hooks';
import { Offering, OfferingStatus } from '@arrived/models';
import { useCanUserTransactEarlyAccess, useSharesAvailableForOfferingQuery } from '@arrived/queries';

import { InvestorsCount } from './InvestorsCount';
import { PropertyGridCardContentTypography } from './PropertyGridCardContentTypography';

const ComingSoon = () => (
  <>
    <AccessTime fontSize="small" />
    <PropertyGridCardContentTypography>
      <FormattedMessage id="common.coming.soon" />
    </PropertyGridCardContentTypography>
  </>
);

const Paused = () => (
  <>
    <AccessTime fontSize="small" />
    <PropertyGridCardContentTypography>
      <FormattedMessage id="common.paused" />
    </PropertyGridCardContentTypography>
  </>
);

interface SellingProps {
  offering: Offering;
}

const Selling = ({ offering }: SellingProps) => {
  const percentFunded = useMemo(() => getPercentFunded(offering), [offering]);
  const sharesAvailableForOfferingState = useSharesAvailableForOfferingQuery(offering.id);

  const isSoldOut = useMemo(
    () =>
      sharesAvailableForOfferingState.isSuccess &&
      sharesAvailableForOfferingState.data.availableSharesCount < getMinSharesToBuy(offering),
    [offering, sharesAvailableForOfferingState]
  );

  return (
    <>
      {isSoldOut ? (
        <Sold lastTransactionDate={offering.lastTransactionDate} />
      ) : (
        <>
          <PropertyGridCardContentTypography>
            <FormattedMessage
              id="percent-funded"
              defaultMessage="{funded}% Funded"
              values={{
                funded: percentFunded,
              }}
            />
          </PropertyGridCardContentTypography>
          <PropertyGridCardContentTypography>|</PropertyGridCardContentTypography>
          <PropertyGridCardContentTypography>
            <InvestorsCount investorsCount={offering.investorsCount} />
          </PropertyGridCardContentTypography>
        </>
      )}
    </>
  );
};

interface SoldProps {
  lastTransactionDate: Offering['lastTransactionDate'];
}

const Sold = ({ lastTransactionDate }: SoldProps) => {
  const timePeriod = useMemo(() => {
    if (lastTransactionDate == null) {
      return;
    }

    // Don't show if the time period is greater than 14 days
    if (differenceInDays(new Date(), parseISO(lastTransactionDate)) > 14) {
      return;
    }

    return formatDistanceToNow(parseISO(lastTransactionDate), { addSuffix: true });
  }, [lastTransactionDate]);

  return (
    <>
      <Done color="success" fontSize="small" />
      <PropertyGridCardContentTypography sx={{ textTransform: 'capitalize' }}>
        {timePeriod == null ? (
          <FormattedMessage id="common.fully.funded" />
        ) : (
          <FormattedMessage
            id="common.fully.funded.time"
            values={{
              timePeriod,
            }}
          />
        )}
      </PropertyGridCardContentTypography>
    </>
  );
};

export interface PropertyGridCardLeftContentProps {
  offering: Offering;
}

export const PropertyGridCardLeftContent = ({ offering }: PropertyGridCardLeftContentProps) => {
  const canUserTransactEarlyAccess = useCanUserTransactEarlyAccess();
  const isOfferingBlocked = useIsOfferingBlocked(offering.shortName);

  const content = useMemo(() => {
    switch (offering.status) {
      case OfferingStatus.PRE_LAUNCH:
      case OfferingStatus.COMING_SOON:
        return <ComingSoon />;
      case OfferingStatus.TRANSACT_EARLY_ACCESS:
        if (canUserTransactEarlyAccess) {
          return <Selling offering={offering} />;
        } else {
          return <ComingSoon />;
        }
      case OfferingStatus.TRANSACT_ALL:
        return <Selling offering={offering} />;
      case OfferingStatus.PAUSED:
        return <Paused />;
      default:
        return <Sold lastTransactionDate={offering.lastTransactionDate} />;
    }
  }, [offering]);

  if (isOfferingBlocked) {
    return <Paused />;
  }

  return (
    <Stack color={({ palette }) => palette.darkened.steel} direction="row" gap={1} alignItems="center">
      {content}
    </Stack>
  );
};
